/**
 * Reusable confirmation dialog — thin wrapper over shadcn AlertDialog to
 * eliminate `window.confirm()` calls scattered across the app.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   ...
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Remove Interakt key?"
 *     description="WhatsApp delivery will stop until reconfigured."
 *     confirmLabel="Remove"
 *     destructive
 *     onConfirm={async () => { ... }}
 *     testid="confirm-remove-interakt"
 *   />
 */
import { useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function ConfirmDialog({
  open,
  onOpenChange,
  title = "Are you sure?",
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  testid = "confirm-dialog",
}) {
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    if (!onConfirm) { onOpenChange?.(false); return; }
    setBusy(true);
    try { await onConfirm(); }
    finally { setBusy(false); onOpenChange?.(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-none" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid={`${testid}-cancel`}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className={`rounded-none ${destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
            onClick={handleConfirm}
            disabled={busy}
            data-testid={`${testid}-confirm`}
          >
            {busy ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
